/* eslint-disable camelcase */
import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useSelector } from "react-redux";
import usePostRequest from "../../hooks/usePostRequest";
import Dialog from "../Dialog";
import "./FileUploadPreSignedUrl.css";

const FileUploadPreSignedUrl = ({
  field,
  accept = "",
  presignUrlEndpoint,
  completeUploadEndpoint,
  cloudFrontBaseUrl,
  agentId,
  triggerRef,
  hideButton = false,
  onPresignSuccess,
  onUploadComplete,
}) => {
  const tokens = useSelector((state) => state?.tokens.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const userId = useSelector((state) => state.user.value?.id);
  const fileInputRef = useRef();

  const { sendPostRequest: getPresignedUrls } = usePostRequest(
    `${presignUrlEndpoint}?app_id=${currentApp.id}&user_id=${userId}${agentId ? `&agent_id=${agentId}` : ""}`,
    tokens
  );

  const { sendPostRequest: completeUpload } = usePostRequest(
    completeUploadEndpoint,
    tokens
  );

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [useLlmExtraction, setUseLlmExtraction] = useState(false);

  // Expose trigger so parent (e.g. TopBar button) can open the upload dialog
  useEffect(() => {
    if (triggerRef) triggerRef.current = handleButtonClick;
  }); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (uploading) {
        event.preventDefault();
        event.returnValue = ""; // Standard for most browsers
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [uploading]);

  const handleButtonClick = () => {
    setUploadModalOpen(true);
  };

  const handleModalClose = () => {
    setUploadModalOpen(false);
    setUseLlmExtraction(false);
  };

  const handleFileSelectClick = () => {
    fileInputRef.current.click();
  };

  const sanitizeFileName = (fileName) => {
    // Replace problematic characters with safe alternatives
    return fileName.replace(/[,+]/g, "_");
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Close modal and proceed with upload
    setUploadModalOpen(false);
    await handleUploadConfirm(files);
  };

  const handleUploadConfirm = async (files) => {
    if (!files || files.length === 0) return;

    const fileNames = files.map((file) => sanitizeFileName(file.name));
    setUploading(true);

    try {
      const presignedResponse = await getPresignedUrls({
        file_names: fileNames,
        use_llm_extraction: useLlmExtraction,
      });

      const { presigned_urls } = presignedResponse;

      if (onPresignSuccess) onPresignSuccess();

      await Promise.all(
        files.map((file, index) => {
          const { url, url_id, s3_path } = presigned_urls[index];

          return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", url);

            xhr.upload.onprogress = (event) => {
              if (event.lengthComputable) {
                const percentComplete = (event.loaded / event.total) * 100;
                setProgress((prevProgress) => ({
                  ...prevProgress,
                  [file.name]: percentComplete,
                }));
              }
            };

            xhr.onload = async () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                await completeUpload({
                  url_id: url_id,
                  s3_path: s3_path,
                });
                resolve();
              } else {
                reject(new Error("Failed to upload file"));
              }
            };

            xhr.onerror = () => reject(new Error("Failed to upload file"));
            xhr.setRequestHeader("Content-Type", file.type);
            xhr.send(file);
          });
        })
      );

      if (onUploadComplete) onUploadComplete();
    } catch (err) {
      console.error("Failed to upload files", err);
      alert("Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
      setProgress({});
      setUseLlmExtraction(false);
    }
  };

  return (
    <div className="form-field">
      <Typography variant="h6">{field.label}</Typography>
      <div className="upload-container" style={hideButton ? { display: "none" } : {}}>
        <input
          type="file"
          multiple={field.multiple}
          accept={accept}
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
        />
        <Button
          variant="outlined"
          className="upload-button"
          onClick={handleButtonClick}
          disabled={uploading}
        >
          {uploading ? (
            <CircularProgress size={24} />
          ) : (
            field.title || "Upload Files"
          )}
        </Button>
      </div>
      {uploading && (
        <div className="upload-progress-card">
          {Object.keys(progress).map((fileName) => (
            <div key={fileName} className="progress-item">
              <Typography variant="caption">{fileName}</Typography>
              <LinearProgress
                variant="determinate"
                value={progress[fileName]}
                className="progress-bar"
              />
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={uploadModalOpen}
        onClose={handleModalClose}
        title="Upload Options"
        content={
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={useLlmExtraction}
                  onChange={(e) => setUseLlmExtraction(e.target.checked)}
                  color="primary"
                />
              }
              label="Use LLM Extraction"
            />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              When enabled, files will be processed using LLM for improved text
              extraction.
            </Typography>
          </>
        }
        actions={
          <>
            <Button
              onClick={handleModalClose}
              variant="outlined"
              size="small"
              sx={{ borderColor: "#cbd5e1", color: "#475569", textTransform: "none", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFileSelectClick}
              variant="contained"
              size="small"
              sx={{ textTransform: "none", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
            >
              Select Files
            </Button>
          </>
        }
      />
    </div>
  );
};

export default FileUploadPreSignedUrl;
